import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "banner", 
    "preferences", 
    "performanceToggle", 
    "functionalityToggle", 
    "advertisingToggle"
  ]
  static values = { googleAnalyticsMeasurementId: String }

  connect() {
    const consent = this.getConsent()
    const consentCookie = this.getCookie("cookie_consent")

    if (!consentCookie || !consent || !consent.timestamp) {
      this.showBanner()
    } else {
      const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000)
      if (consent.timestamp < oneYearAgo) {
        this.showBanner()
      } else {
        this.applyCookiePreferences(consent)
      }
    }

    this.boundOpenPreferencesHandler = this.showPreferences.bind(this)
    window.addEventListener('openCookiePreferences', this.boundOpenPreferencesHandler)
  }

  disconnect() {
    // Clean up event listener
    if (this.boundOpenPreferencesHandler) {
      window.removeEventListener('openCookiePreferences', this.boundOpenPreferencesHandler)
    }
  }

  // Accept all cookies
  acceptAll() {
    const consent = {
      essential: true,
      performance: true,
      functionality: true,
      advertising: true,
      timestamp: Date.now(),
      version: "1.0"
    }
    this.saveConsent(consent)
    this.applyCookiePreferences(consent)
    this.hideBanner()
  }

  // Reject non-essential cookies
  rejectNonEssential() {
    const consent = {
      essential: true,
      performance: false,
      functionality: false,
      advertising: false,
      timestamp: Date.now(),
      version: "1.0"
    }
    this.saveConsent(consent)
    this.applyCookiePreferences(consent)
    this.hideBanner()
  }

  // Show preferences modal
  showPreferences() {
    // Load current preferences into toggles
    const consent = this.getConsent()
    if (consent) {
      this.performanceToggleTarget.checked = consent.performance !== false
      this.functionalityToggleTarget.checked = consent.functionality !== false
      this.advertisingToggleTarget.checked = consent.advertising !== false
    }
    
    this.preferencesTarget.classList.remove("hidden")
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden"
  }

  // Close preferences modal
  closePreferences() {
    this.preferencesTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // Save custom preferences
  savePreferences() {
    const consent = {
      essential: true, // Always true
      performance: this.performanceToggleTarget.checked,
      functionality: this.functionalityToggleTarget.checked,
      advertising: this.advertisingToggleTarget.checked,
      timestamp: Date.now(),
      version: "1.0"
    }
    this.saveConsent(consent)
    this.applyCookiePreferences(consent)
    this.closePreferences()
    this.hideBanner()
  }

  // Helper methods
  getConsent() {
    try {
      const stored = localStorage.getItem("cookieConsent")
      if (stored) {
        // Handle legacy format (simple string)
        if (stored === "accepted" || stored === "rejected") {
          return null // Force re-consent with new format
        }
        return JSON.parse(stored)
      }
    } catch (e) {
      console.error("Error reading cookie consent:", e)
    }
    return null
  }

  saveConsent(consent) {
    try {
      localStorage.setItem("cookieConsent", JSON.stringify(consent))
      
      // Also set a server-side cookie for backend tracking if needed
      this.setCookie("cookie_consent", consent.timestamp, 365)
    } catch (e) {
      console.error("Error saving cookie consent:", e)
    }
  }

  applyCookiePreferences(consent) {
    this.currentConsent = consent
    
    if (consent.performance) {
      this.enablePerformanceCookies()
    } else {
      this.disablePerformanceCookies()
    }

    if (consent.functionality) {
      this.enableFunctionalityCookies()
    } else {
      this.disableFunctionalityCookies()
    }

    if (consent.advertising) {
      this.enableAdvertisingCookies()
    } else {
      this.disableAdvertisingCookies()
    }

    this.updateGoogleAnalyticsConsent(consent)
    if (consent.performance) this.trackGoogleAnalyticsPageView()

    // Dispatch custom event that other scripts can listen to
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
      detail: consent
    }))
  }

  enablePerformanceCookies() {
    const measurementId = this.googleAnalyticsMeasurementId
    if (!measurementId) return

    window[`ga-disable-${measurementId}`] = false
    window.dataLayer = window.dataLayer || []
    window.gtag = window.gtag || function() { window.dataLayer.push(arguments) }

    window.WeBuildGoogleAnalyticsInitialized = window.WeBuildGoogleAnalyticsInitialized || {}
    if (!window.WeBuildGoogleAnalyticsInitialized[measurementId]) {
      window.gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied"
      })
      window.gtag("js", new Date())

      const script = document.createElement("script")
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
      script.dataset.googleAnalyticsMeasurementId = measurementId
      document.head.appendChild(script)

      window.WeBuildGoogleAnalyticsInitialized[measurementId] = true
    }
  }

  disablePerformanceCookies() {
    const measurementId = this.googleAnalyticsMeasurementId
    if (measurementId) window[`ga-disable-${measurementId}`] = true
  }

  enableFunctionalityCookies() {
    console.log("Functionality cookies enabled")
  }

  disableFunctionalityCookies() {
    console.log("Functionality cookies disabled")
  }

  enableAdvertisingCookies() {
    console.log("Advertising cookies enabled")
  }

  disableAdvertisingCookies() {
    console.log("Advertising cookies disabled")
  }

  updateGoogleAnalyticsConsent(consent) {
    if (!this.googleAnalyticsMeasurementId || typeof window.gtag !== "function") return

    const performanceConsent = consent.performance ? "granted" : "denied"
    const advertisingConsent = consent.advertising ? "granted" : "denied"
    window.gtag("consent", "update", {
      analytics_storage: performanceConsent,
      ad_storage: advertisingConsent,
      ad_user_data: advertisingConsent,
      ad_personalization: advertisingConsent
    })
  }

  trackGoogleAnalyticsPageView() {
    const measurementId = this.googleAnalyticsMeasurementId
    if (!measurementId || typeof window.gtag !== "function") return

    window.gtag("config", measurementId, {
      anonymize_ip: true,
      page_location: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_title: document.title
    })
  }

  get googleAnalyticsMeasurementId() {
    return this.googleAnalyticsMeasurementIdValue.toString().trim()
  }

  setCookie(name, value, days) {
    const expires = new Date()
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
  }

  getCookie(name) {
    const nameEQ = `${name}=`
    const parts = document.cookie.split(';')
    for (let i = 0; i < parts.length; i += 1) {
      let c = parts[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
  }

  showBanner() {
    this.bannerTarget.classList.remove("hidden")
  }

  hideBanner() {
    this.bannerTarget.classList.add("hidden")
  }
}
